import { useMemo } from "react";

import type { ProWebsiteFeaturesNoTouchJourneyAddonPurchaseInfoResponse } from "@similarweb/pro-api-client";

import type { ISwSettings } from "app/@types/ISwSettings";
import { useNoTouchPurchaseDetailsQuery } from "app/components/no-touch-purchase-modal/summary-modal/details/useNoTouchPurchaseDetailsQuery";
import type { INoTouchAddon } from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { DEFAULT_CURRENCY } from "app/common/constants/currencies";
import { isMonthlyPaymentPeriod } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

interface IUseNoTouchPurchaseSummaryModalDetailsApi {
    isMonthly: boolean;
    isLoading: boolean;
    purchaseDetails?: IPurchaseDetails;
    vatPercent?: number;
}

interface IUseNoTouchPurchaseSummaryModalDetailsProps {
    swSettings: ISwSettings;
    selectedAddon?: INoTouchAddon;
}

export interface IPurchaseDetails {
    price: number;
    dueToday: number;
    nextBillingPeriod: Date;
    tax: number;
    currency: string;
}

const parseDTO = (
    resource: ProWebsiteFeaturesNoTouchJourneyAddonPurchaseInfoResponse,
): IPurchaseDetails | undefined => {
    if (!resource || !resource.products || !resource.products[0]) {
        return undefined;
    }

    const item = resource.products[0];
    return {
        price: item.price,
        nextBillingPeriod: new Date(item.nextBillingPeriod),
        dueToday: Number(item.relativePrice.toFixed(0)) + Number(item.relativeTaxAmount.toFixed(0)),
        tax: item.taxAmount,
        currency: item.currency ?? DEFAULT_CURRENCY,
    };
};

export const useNoTouchPurchaseSummaryModalDetails = ({
    swSettings,
    selectedAddon,
}: IUseNoTouchPurchaseSummaryModalDetailsProps): IUseNoTouchPurchaseSummaryModalDetailsApi => {
    const isMonthly = isMonthlyPaymentPeriod(swSettings);

    const { isLoading, isError, data } = useNoTouchPurchaseDetailsQuery({ selectedAddon });

    const parsedData = useMemo(
        (): IPurchaseDetails | undefined => (isLoading || isError ? undefined : parseDTO(data)),
        [data, isError, isLoading],
    );

    const vatPercent = useMemo(() => {
        if (!parsedData || (parsedData?.price ?? 0) === 0) {
            return undefined;
        }

        return Math.round((parsedData.tax / parsedData.price) * 100);
    }, [parsedData]);

    return {
        isMonthly,
        isLoading,
        vatPercent,
        purchaseDetails: parsedData,
    };
};
